import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Psychology as PsychologyIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { CoreTopicDTO } from '../../services/adminCourseService';

interface LLMAssistantDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (query: string, options: LLMQueryOptions) => void;
  subject: { id: number; code: string; name: string; description?: string };
  existingTopics: CoreTopicDTO[];
  eoHierarchy?: any[];
  loading?: boolean;
  progress?: number;
  progressMessage?: string;
}

export interface LLMQueryOptions {
  includePrerequisites: boolean;
  suggestEdits: boolean;
  suggestDeletions: boolean;
  selectedTopicIds?: number[]; // For segment-based queries
}

const LLMAssistantDialog: React.FC<LLMAssistantDialogProps> = ({
  open,
  onClose,
  onSubmit,
  subject,
  existingTopics,
  eoHierarchy = [],
  loading = false,
  progress = 0,
  progressMessage = '',
}) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<LLMQueryOptions>({
    includePrerequisites: true,
    suggestEdits: true,
    suggestDeletions: false,
  });

  const handleSubmit = () => {
    if (!query.trim()) {
      return;
    }
    onSubmit(query.trim(), options);
  };

  const getAllTopicsFlat = (topics: CoreTopicDTO[]): CoreTopicDTO[] => {
    const result: CoreTopicDTO[] = [];
    const traverse = (ts: CoreTopicDTO[]) => {
      ts.forEach(t => {
        result.push(t);
        if (t.children) traverse(t.children);
      });
    };
    traverse(topics);
    return result;
  };

  const flatTopics = getAllTopicsFlat(existingTopics);
  const topicCount = flatTopics.length;
  const rootTopicsCount = existingTopics.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PsychologyIcon color="primary" />
          <Typography variant="h6">AI Topic Assistant</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Analyzing your requirements...
            </Typography>
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {progressMessage || `${progress}% complete`}
              </Typography>
            </Box>
            <Box sx={{ mt: 3, textAlign: 'left' }}>
              <Typography variant="body2" color="text.secondary">
                • Analyzing subject context...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Reviewing existing topics...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Generating topic suggestions...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Building relationships...
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Context Panel */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <InfoIcon fontSize="small" />
                  <Typography variant="subtitle1">Context Information</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Subject: {subject.code} - {subject.name}
                  </Typography>
                  {subject.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {subject.description}
                    </Typography>
                  )}
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      Existing Topics:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • {rootTopicsCount} root topics
                      • {topicCount} total topics
                    </Typography>
                  </Box>

                  {eoHierarchy.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        Experience Outcomes:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • {eoHierarchy.length} curriculum areas
                        • {eoHierarchy.reduce((sum, area) => 
                          sum + (area.groups?.reduce((gSum: number, group: any) => 
                            gSum + (group.experienceOutcomes?.length || 0), 0) || 0), 0
                        )} experience outcomes
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      Topic Relationships:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Parent-child hierarchies present
                      • Prerequisite chains available
                    </Typography>
                  </Box>
                </Paper>
              </AccordionDetails>
            </Accordion>

            {/* Query Input */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Your Requirements
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder={`Describe what topics you need. The AI will also add: topics for Secondary school [this subject], in parent-child hierarchy, so each topic can be covered in 20-30 minutes and assessed standalone.\n\nExamples:\n\nAdd topics for basic algebra suitable for S1 students. Focus on linear equations and inequalities.\n\nOr: Design topics for Senior Phase, covering calculus fundamentals. Link to existing Third/Fourth level topics as prerequisites.`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                variant="outlined"
                helperText={`${query.length} characters`}
              />
            </Box>

            {/* Options */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Options
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={options.includePrerequisites}
                      onChange={(e) =>
                        setOptions({ ...options, includePrerequisites: e.target.checked })
                      }
                    />
                  }
                  label="Include prerequisite relationships for new topics"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={options.suggestEdits}
                      onChange={(e) =>
                        setOptions({ ...options, suggestEdits: e.target.checked })
                      }
                    />
                  }
                  label="Suggest edits to existing topics"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={options.suggestDeletions}
                      onChange={(e) =>
                        setOptions({ ...options, suggestDeletions: e.target.checked })
                      }
                    />
                  }
                  label="Suggest deletions of topics that don't fit"
                />
              </Box>
            </Box>

            {/* Examples */}
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(33, 150, 243, 0.08)' }}>
              <Typography variant="subtitle2" gutterBottom>
                💡 Example Queries:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Chip
                  label="Add topics for basic algebra suitable for beginners"
                  size="small"
                  onClick={() => setQuery('Add topics for basic algebra suitable for beginners')}
                  sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.5 }}
                />
                <Chip
                  label="Design Senior Phase topics with prerequisites from existing Third/Fourth level topics"
                  size="small"
                  onClick={() => setQuery('Design Senior Phase topics with prerequisites from existing Third/Fourth level topics')}
                  sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.5 }}
                />
                <Chip
                  label="Review and improve existing topic structure, suggest better organization"
                  size="small"
                  onClick={() => setQuery('Review and improve existing topic structure, suggest better organization')}
                  sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.5 }}
                />
                <Chip
                  label="Add topics for Secondary school (this subject), parent-child hierarchy, 20-30 min per topic, assessable standalone"
                  size="small"
                  onClick={() => setQuery('Add topics for Secondary school, create them in parent-child hierarchy to a level where one topic can be covered in one setting (20-30 minutes) and can be assessed standalone.')}
                  sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.5 }}
                />
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!query.trim() || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <PsychologyIcon />}
        >
          {loading ? 'Processing...' : 'Get Suggestions'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LLMAssistantDialog;

